import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, checkBoxHelper, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { formPaths } from '@shared/constants/form-paths.const';
import { getCoopConsumoControl } from './helpers/coop-consumo-form.helpers';

export const schedaCoopConsumoRules: FormGroupRules = {
    my_CkVendeAiNonSoci: (form: FormGroup) => {
        baseRequiredCondition(form, 'my_CkVendeAiNonSoci');
    },
    my_PuntiVendita: (form: FormGroup) => {
        baseRequiredCondition(form, 'my_PuntiVendita');
    },
    my_CkOffreAltriServizi: (form: FormGroup) => {
        baseRequiredCondition(form, 'my_CkOffreAltriServizi');
    },
    my_CkGestisceSezionePrestitoSociale: (form: FormGroup) => {
        baseRequiredCondition(form, 'my_CkGestisceSezionePrestitoSociale');
    },

    my_CkSettoriATerzi: (form: FormGroup) => {
        baseRequiredCondition(form, 'my_CkSettoriATerzi');
    },

    my_CkRichiesteLicenze: (form: FormGroup) => {
        const schedaGroup = getGroup(form, formPaths.schedaCoopConsumo);
        const control = getControl(schedaGroup, 'my_CkRichiesteLicenze');
        const puntiVendita: string = getControl(schedaGroup, 'my_PuntiVendita').value;
        const scConsumoObbl: string = getControl(schedaGroup, 'my_ScConsumoObbl').value;
        const errors = control.errors ?? {};
        const hasError = isNilOrEmpty(control.value) && toNum(puntiVendita) > 0 && (scConsumoObbl === '1' || tipoSezCkAltreCoopHelper(form));

        addRemoveError(hasError, errors, 'my_CkRichiesteLicenze', {
            status: REQUIRED_STATUS,
        });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkPossiedeLicenze: (form: FormGroup) => {
        const schedaGroup = getGroup(form, formPaths.schedaCoopConsumo);
        const control = getControl(schedaGroup, 'my_CkPossiedeLicenze');
        const ckRichiesteLicenze: string = getControl(schedaGroup, 'my_CkRichiesteLicenze').value;
        const puntiVendita: string = getControl(schedaGroup, 'my_PuntiVendita').value;
        const scConsumoObbl: string = getControl(schedaGroup, 'my_ScConsumoObbl').value;
        const errors = control.errors ?? {};
        const hasError = isNilOrEmpty(control.value) && ckRichiesteLicenze === '1' && toNum(puntiVendita) > 0 && (scConsumoObbl === '1' || tipoSezCkAltreCoopHelper(form));
        addRemoveError(hasError, errors, 'my_CkPossiedeLicenze', {
            status: REQUIRED_STATUS,
        });
        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_coopConsGenAlimVend: (form: FormGroup) => {
        generiVendutiHelper(form);
    },

    my_SettoriInGestioneTerzi: (form: FormGroup) => {
        ckHelper(form, 'my_SettoriInGestioneTerzi', 'my_CkSettoriATerzi', 1);
    },

    my_GeneriVenduti: (form: FormGroup) => {
        ckHelper(form, 'my_GeneriVenduti', 'my_coopConsAltriGenVend', 1);
    },
    my_AltriServiziOfferti: (form: FormGroup) => {
        ckHelper(form, 'my_AltriServiziOfferti', 'my_CkOffreAltriServizi', 1);
    },
    my_CommentoGestionePrestitoSociale: (form: FormGroup) => {
        ckHelper(form, 'my_CommentoGestionePrestitoSociale', 'my_CkGestisceSezionePrestitoSociale', 1);
    },
};

const baseRequiredCondition = (form: FormGroup, targetPath: string): void => {
    const control = getCoopConsumoControl(form, targetPath);
    const scConsumoObbl: string = getCoopConsumoControl(form, 'my_ScConsumoObbl').value;
    const errors = control.errors ?? {};
    const hasError = isNilOrEmpty(control.value) && (tipoSezCkAltreCoopHelper(form) || scConsumoObbl === '1');

    addRemoveError(hasError, errors, targetPath, { status: REQUIRED_STATUS });

    control.setErrors(isEqual(errors, {}) ? null : errors);
};

const ckHelper = (form: FormGroup, targetPath: string, ckPath: string, ckTargetValue: number, suppCondition = true, errorStatus?: string): void => {
    const scConsumoObbl: string = getCoopConsumoControl(form, 'my_ScConsumoObbl').value;

    checkBoxHelper(form, targetPath, ckPath, ckTargetValue, formPaths.schedaCoopConsumo, suppCondition && (scConsumoObbl === '1' || tipoSezCkAltreCoopHelper(form)), errorStatus);
};

const generiVendutiHelper = (form: FormGroup): void => {
    const genAlimControl = getCoopConsumoControl(form, 'my_coopConsGenAlimVend');
    const altriGenControl = getCoopConsumoControl(form, 'my_coopConsAltriGenVend');
    const scConsumoObbl: string = getCoopConsumoControl(form, 'my_ScConsumoObbl').value;
    const genAlimErrors = genAlimControl.errors ?? {};
    const altriGenErrors = altriGenControl.errors ?? {};

    const noneSelected = toNum(genAlimControl.value) === 0 && toNum(altriGenControl.value) === 0;
    const hasError = noneSelected && (scConsumoObbl === '1' || tipoSezCkAltreCoopHelper(form));

    addRemoveError(hasError, genAlimErrors, 'my_coopConsGenAlimVend', { status: REQUIRED_STATUS });
    addRemoveError(hasError, altriGenErrors, 'my_coopConsAltriGenVend', { status: REQUIRED_STATUS });

    genAlimControl.setErrors(isEqual(genAlimErrors, {}) ? null : genAlimErrors);
    altriGenControl.setErrors(isEqual(altriGenErrors, {}) ? null : altriGenErrors);
};

const tipoSezCkAltreCoopHelper = (form: FormGroup) => {
    const my_TipoSezEnte: string = getControl(form, `${formPaths.datiIdentificativi}.my_TipoSezEnte`).value;
    const my_CkAltreCoopAllegaSchedaConsumo: string = getControl(form, `${formPaths.schedaAltreCoop}.my_CkAltreCoopAllegaSchedaConsumo`).value;
    return my_TipoSezEnte === '10' && my_CkAltreCoopAllegaSchedaConsumo === '1';
};

export const CoopConsumoFormValidators = Object.values(schedaCoopConsumoRules);
